import {
  ArrowLeftRight,
  ChevronDown,
  EyeOff,
  ImagePlus,
  KeyRound,
  LogOut,
  Presentation,
  Trash2,
  User,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/features/auth/auth-context';
import { BildirimZili } from '@/features/notifications/bildirim-zili';
import { SaatKurDugmesi } from './saat-kur-seridi';
import { TemaSecici } from './tema-secici';
import { digerPaneller } from '@/lib/pazar';
import { cn } from '@/lib/utils';
import { panelAdresi, sunumModunuAyarla, useSunumModu } from '@/lib/sunum';
import { useProfilResmi, useProfilResmiIslemleri } from '@/features/account/profil-resmi';

/** "Kerim Samet Kılıç" → "KS". Ad yoksa `undefined` (simge gösterilir). */
function basHarfler(ad: string | undefined): string | undefined {
  // Kelimenin ilk HARFİ: "Önizleme (yerel test)" → "ÖY", parantez değil
  const harfler = (ad ?? '').match(/\p{L}[\p{L}\p{M}'’.-]*/gu) ?? [];
  if (harfler.length === 0) return undefined;
  return harfler
    .slice(0, 2)
    .map((k) => k.charAt(0).toLocaleUpperCase('tr-TR'))
    .join('');
}

/** Şifre, iki adımlı doğrulama ve açık oturumlar bu ekranda. */
const HESAP_GUVENLIGI: string = '/ayarlar/kullanicilar';

const ROLE_LABEL: Record<string, string> = {
  OWNER: 'Sahip',
  ADMIN: 'Yönetici',
  VIEWER: 'İzleyici',
};

/**
 * Üst çubuğun sağındaki araçlar: saat/kur, bildirim zili, tema, hesap menüsü.
 *
 * 28 Eyl 2026: menü üste taşındı (`top-nav.tsx`); bu araçlar artık onun sağında.
 */
export function TopbarAraclari() {
  const { user, logout } = useAuth();
  const profilResmi = useProfilResmi(user?.email);
  const profil = useProfilResmiIslemleri();
  const resimSecici = useRef<HTMLInputElement>(null);
  const [resimHatasi, setResimHatasi] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  /** Hesap menüsündeki "Başka panele geç" listesi (23 Eki 2026: alt alta değil, açılır). */
  const [panellerAcik, setPanellerAcik] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Sunum modu: kâr, maliyet, ciro ve müşteri bilgileri bulanık (22 Eki 2026)
  const sunum = useSunumModu();

  // Menü kapanınca panel listesi de kapansın; yeniden açılışta kısa hâliyle gelsin
  useEffect(() => {
    if (!menuOpen) setPanellerAcik(false);
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  return (
    <div className="flex items-center gap-1">
      {/*
        Saat ve kur düğmesi tema düğmesinin SOLUNDA: göz sağdan sola
        giderken önce eylem düğmelerini, sonra bilgiyi buluyor. Tam şerit
        çubuğa sığmıyordu (29 Eyl 2026); burada yalnız pazar saati + kuru, tıklayınca
        hepsi. Daha dar ekranda sığmıyor; orada aynı düğme ana sayfanın başlığında.
      */}
      <SaatKurDugmesi className="mr-1 hidden 5xl:block" />

      {/*
        Zil tema düğmesinin SOLUNDA: uyarılar bir görünüm tercihinden daha
        önemli, göz sağdan sola giderken önce hesap menüsünü, sonra temayı,
        sonra bildirimleri buluyor.
      */}
      <BildirimZili />

      {/* Açıkken görünür kalsın: sahip modun açık olduğunu her an görmeli */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => sunumModunuAyarla(!sunum)}
        aria-pressed={sunum}
        aria-label={sunum ? 'Sunum modunu kapat' : 'Sunum modunu aç'}
        title={
          sunum
            ? 'Sunum modu açık — kâr, maliyet ve müşteri bilgileri gizli. Kapatmak için tıklayın.'
            : 'Sunum modu: ekran paylaşırken kâr, maliyet ve müşteri bilgilerini gizle'
        }
        className={sunum ? 'bg-warning-soft text-warning hover:bg-warning-soft' : undefined}
        data-testid="sunum-modu"
      >
        {sunum ? <EyeOff aria-hidden="true" /> : <Presentation aria-hidden="true" />}
      </Button>

      <TemaSecici />

      <div className="relative" ref={menuRef}>
        {/*
          Yalnız baş harfler (29 Eyl 2026): ad yazılı düğme 160px'e kadar yer
          tutup üst menüyü taşırıyordu. Ad ve e-posta açılan menünün başında.
        */}
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          aria-label={`Hesap menüsü: ${user?.name ?? 'Kullanıcı'}`}
          title={user?.name ?? 'Kullanıcı'}
          className="ml-1 grid size-8 place-items-center overflow-hidden rounded-full bg-accent-soft text-xs font-semibold text-accent ring-1 ring-accent/20 transition-shadow hover:ring-2 hover:ring-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          {profilResmi !== null ? (
            <img src={profilResmi} alt="" className="size-full object-cover" />
          ) : (
            (basHarfler(user?.name) ?? <User className="size-4" aria-hidden="true" />)
          )}
        </button>
        <input
          ref={resimSecici}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => {
            const dosya = e.target.files?.[0];
            e.target.value = '';
            if (!dosya) return;
            setResimHatasi(null);
            profil.yukle(dosya).catch((h: unknown) => {
              setResimHatasi(h instanceof Error ? h.message : 'Resim yüklenemedi.');
              setMenuOpen(true);
            });
          }}
        />

        {menuOpen && (
          <div
            role="menu"
            className="absolute right-0 z-50 mt-2 w-56 rounded-[var(--radius)] border border-border bg-bg-elevated p-1 shadow-[var(--shadow-md)]"
          >
            <div className="border-b border-border px-3 py-2">
              <p className="truncate text-sm font-medium text-fg">{user?.name}</p>
              <p className="truncate text-xs text-fg-muted">{user?.email}</p>
              <div className="mt-1.5 flex items-center gap-1.5">
                <Badge tone="accent">{ROLE_LABEL[user?.role ?? ''] ?? user?.role}</Badge>
                {user?.totpEnabled ? (
                  <Badge tone="success">2FA açık</Badge>
                ) : (
                  <Badge tone="warning">2FA kapalı</Badge>
                )}
              </div>
            </div>
            <button
              type="button"
              role="menuitem"
              onClick={() => resimSecici.current?.click()}
              className="flex w-full items-center gap-2 rounded-[var(--radius)] px-3 py-2 text-left text-sm text-fg-muted transition-colors hover:bg-accent-soft hover:text-fg"
            >
              <ImagePlus className="size-4" aria-hidden="true" />
              {profilResmi !== null ? 'Profil resmini değiştir' : 'Profil resmi ekle'}
            </button>
            {profilResmi !== null && (
              <button
                type="button"
                role="menuitem"
                onClick={() => void profil.kaldir()}
                className="flex w-full items-center gap-2 rounded-[var(--radius)] px-3 py-2 text-left text-sm text-fg-muted transition-colors hover:bg-accent-soft hover:text-fg"
              >
                <Trash2 className="size-4" aria-hidden="true" />
                Profil resmini kaldır
              </button>
            )}
            {resimHatasi !== null && <p className="px-3 py-1 text-xs text-danger">{resimHatasi}</p>}
            <Link
              to={HESAP_GUVENLIGI}
              role="menuitem"
              onClick={() => setMenuOpen(false)}
              className="flex w-full items-center gap-2 rounded-[var(--radius)] px-3 py-2 text-left text-sm text-fg-muted transition-colors hover:bg-accent-soft hover:text-fg"
            >
              <KeyRound className="size-4" aria-hidden="true" />
              Şifre ve güvenlik
            </Link>
            {/*
              Paneller arası geçiş (14 Eki 2026): JP ve UK ayrı panel, ayrı oturum.
              Diğer panelde bir kez giriş yapıldıktan sonra geçiş doğrudan.
            */}
            {/*
              23 Eki 2026 (sahip): paneller alt alta yazılmasın, açılır kapanır olsun. Panel
              sayısı arttıkça (21 pazar kodda hazır) menü uzamıyor.
            */}
            {digerPaneller().length > 0 && (
              <div>
                <button
                  type="button"
                  role="menuitem"
                  aria-expanded={panellerAcik}
                  onClick={() => setPanellerAcik((v) => !v)}
                  className="flex w-full items-center gap-2 rounded-[var(--radius)] px-3 py-2 text-left text-sm text-fg-muted transition-colors hover:bg-accent-soft hover:text-fg"
                  data-testid="panel-gecis"
                >
                  <ArrowLeftRight className="size-4" aria-hidden="true" />
                  <span className="flex-1">Başka panele geç</span>
                  <ChevronDown
                    className={cn('size-4 transition-transform', panellerAcik && 'rotate-180')}
                    aria-hidden="true"
                  />
                </button>
                {panellerAcik && (
                  <div className="mb-1 ml-4 border-l border-border pl-1">
                    {digerPaneller().map((p) => (
                      <a
                        key={p.kod}
                        role="menuitem"
                        href={panelAdresi(p.panelAdresi, sunum)}
                        className="flex w-full items-center gap-2 rounded-[var(--radius)] px-3 py-1.5 text-left text-sm text-fg-muted transition-colors hover:bg-accent-soft hover:text-fg"
                      >
                        <span aria-hidden="true">{p.bayrak}</span>
                        {p.kod} — {p.ad}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
            <button
              type="button"
              role="menuitem"
              onClick={() => void logout()}
              className="flex w-full items-center gap-2 rounded-[var(--radius)] px-3 py-2 text-left text-sm text-fg-muted transition-colors hover:bg-danger-soft hover:text-danger"
            >
              <LogOut className="size-4" aria-hidden="true" />
              Çıkış yap
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
